import React, { useEffect, useState } from 'react';
import ListCel, { ListEntry } from './cells/list_cel';
import MediaPickerModal from './MediaPickerModal';
import { listLists, getList, saveList, deleteList, renameList } from '../lib/lists';
import Toast from './Toast';

export type ListEditorProps = {
  open: boolean;
  orientation: 'vertical'|'horizontal';
  initialItems?: string[];
  initialEntries?: ListEntry[];
  initialImageSize?: number;
  initialImageRadius?: number;
  onClose: () => void;
  onSave: (items: string[], entries: ListEntry[], opts: { imageSize: number; imageRadius: number }) => void;
};

const ListEditor: React.FC<ListEditorProps> = ({ open, orientation, initialItems, initialEntries, initialImageSize, initialImageRadius, onClose, onSave }) => {
  const [entries, setEntries] = useState<ListEntry[]>(() => (initialEntries && initialEntries.length)
    ? initialEntries
    : (initialItems || []).map(t => ({ text: t }))
  );
  const [draft, setDraft] = useState('');
  const [pickerFor, setPickerFor] = useState<number | null>(null);
  const [hoverTimer, setHoverTimer] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<number>(initialImageSize ?? 24);
  const [imageRadius, setImageRadius] = useState<number>(initialImageRadius ?? 6);
  const [available, setAvailable] = useState<{ name: string; mtime: number }[]>([]);
  const [listName, setListName] = useState<string>('');
  const [loadingLists, setLoadingLists] = useState<boolean>(false);
  const [toast, setToast] = useState<{ message: string; type: 'success'|'error'|'info' }|null>(null);
  const [showLibrary, setShowLibrary] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<string|null>(null);
  const [renameTarget, setRenameTarget] = useState<{ old: string; value: string } | null>(null);

  useEffect(() => {
    if (open) {
      const base = (initialEntries && initialEntries.length) ? initialEntries : (initialItems || []).map(t => ({ text: t }));
      setEntries(base);
      setDraft('');
      setImageSize(initialImageSize ?? 24);
      setImageRadius(initialImageRadius ?? 6);
      setListName('');
      (async () => {
        try { setLoadingLists(true); const items = await listLists(); setAvailable(items.map(i => ({ name: i.name, mtime: i.mtime }))); }
        catch {} finally { setLoadingLists(false); }
      })();
    }
  }, [open, initialItems, initialEntries, initialImageSize, initialImageRadius]);
  if (!open) return null;

  const addItem = () => {
    const t = draft.trim(); if (!t) return; setEntries(prev => [...prev, { text: t }]); setDraft('');
  };
  const removeAt = (i: number) => setEntries(prev => prev.filter((_, idx) => idx !== i));
  const updateAt = (i: number, v: string) => setEntries(prev => prev.map((it, idx) => idx===i ? { ...it, text: v } : it));
  const setImageAt = (i: number, url?: string) => setEntries(prev => prev.map((it, idx) => idx===i ? { ...it, image: url } : it));
  const startHoverPreview = (url?: string) => {
    if (!url) return;
    if (hoverTimer) window.clearTimeout(hoverTimer);
    const t = window.setTimeout(() => setPreviewUrl(url), 800) as unknown as number;
    setHoverTimer(t);
  };
  const cancelHoverPreview = () => {
    if (hoverTimer) window.clearTimeout(hoverTimer);
    setHoverTimer(null);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50" onMouseDown={onClose}>
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-4xl max-h-[85vh] overflow-hidden" onMouseDown={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-purple-50">
              <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="3" cy="6" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="18" r="1"/></svg>
            </div>
            <div className="font-semibold text-gray-900">List Editor</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <button
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm min-w-[180px] inline-flex items-center justify-between gap-2"
                onClick={()=> setShowLibrary(v=>!v)}
                aria-haspopup="listbox"
                aria-expanded={showLibrary}
              >
                <span className="truncate">{loadingLists ? 'Loading…' : (listName || (available.length ? 'Select list…' : 'No lists found'))}</span>
                <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              {showLibrary && (
                <div className="absolute z-[150] mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg">
                  <div className="max-h-64 overflow-auto">
                    {available.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">{loadingLists ? 'Loading…' : 'No lists found'}</div>
                    ) : (
                      available.map(it => (
                        <div key={it.name} className="px-2 py-1.5 flex items-center gap-2 hover:bg-gray-50">
                          <button
                            className="flex-1 text-left text-sm text-gray-800 truncate"
                            onClick={async ()=>{
                              setShowLibrary(false);
                              setListName(it.name);
                              try { const lst = await getList(it.name); if (lst) { setEntries(lst.entries || []); if (typeof lst.imageSize === 'number') setImageSize(lst.imageSize); if (typeof lst.imageRadius === 'number') setImageRadius(lst.imageRadius); } }
                              catch {}
                            }}
                          >{it.name}</button>
                          <button
                            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-gray-100"
                            title="Rename"
                            onClick={()=>{ setShowLibrary(false); setRenameTarget({ old: it.name, value: it.name }); }}
                            aria-label="Rename"
                          >
                            <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                          </button>
                          <button
                            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-gray-100"
                            title="Delete"
                            onClick={()=>{ setShowLibrary(false); setConfirmDelete(it.name); }}
                            aria-label="Delete"
                          >
                            <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M8 6v12"/><path d="M16 6v12"/><path d="M5 6l1 14a2 2 0 002 2h8a2 2 0 002-2l1-14"/><path d="M10 6V4a2 2 0 012-2 2 2 0 012 2v2"/></svg>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <input
              className="h-8 rounded-md border border-gray-200 px-2 text-sm"
              placeholder="List name"
              value={listName}
              onChange={e=>setListName(e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,''))}
              style={{ width: 160 }}
            />
            <button
              className={`px-3 h-8 rounded-md text-white ${listName && entries.length>0 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-300 cursor-not-allowed'}`}
              disabled={!listName || entries.length===0}
              aria-disabled={!listName || entries.length===0}
              onClick={async()=>{
                let name = (listName || '').trim();
                if (!name) { setToast({ message: 'Please provide a list name before saving.', type: 'error' }); return; }
                if (entries.length === 0) { setToast({ message: 'Add at least one list item before saving.', type: 'error' }); return; }
                try {
                  await saveList(name, { entries, imageSize, imageRadius, items: entries.map(e=>e.text) });
                  const items = await listLists(); setAvailable(items.map(i => ({ name: i.name, mtime: i.mtime })));
                } catch { setToast({ message: 'Failed to save list to library.', type: 'error' }); }
                onSave(entries.map(e=>e.text), entries, { imageSize, imageRadius });
              }}
            >Save</button>
            <button
              className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100"
              title="Close"
              aria-label="Close"
              onClick={onClose}
            >
              <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="p-4 border-r border-gray-100">
            <div className="mb-3 flex items-center gap-2">
              <input
                value={draft}
                onChange={e=>setDraft(e.target.value)}
                placeholder="Add a list item…"
                className="flex-1 h-9 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
              <button className="h-9 px-3 rounded-md bg-purple-600 hover:bg-purple-700 text-white" onClick={addItem}>Add</button>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-3">
              <label className="text-xs text-gray-600 flex flex-col gap-1">
                <span>Image size: <span className="font-medium text-gray-800">{imageSize}px</span></span>
                <input type="range" min={16} max={120} value={imageSize} onChange={e=>setImageSize(Number(e.target.value))} />
              </label>
              <label className="text-xs text-gray-600 flex flex-col gap-1">
                <span>Corner radius: <span className="font-medium text-gray-800">{imageRadius}px</span></span>
                <input type="range" min={0} max={64} value={imageRadius} onChange={e=>setImageRadius(Number(e.target.value))} />
              </label>
            </div>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto overflow-x-hidden pr-1">
              {entries.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white/60 p-6 text-center text-gray-500">
                  <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-purple-50 ring-1 ring-purple-200 flex items-center justify-center">
                    <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="3" cy="6" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="18" r="1"/></svg>
                  </div>
                  <div className="text-sm font-medium">No items yet</div>
                  <div className="text-xs">Add your first item above to get started.</div>
                </div>
              ) : (
                <>
                  {entries.map((it, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div
                        className="relative h-9 w-9 shrink-0"
                        onMouseEnter={()=> startHoverPreview(it.image)}
                        onMouseLeave={cancelHoverPreview}
                      >
                        <button
                          className="absolute inset-0 rounded-md border border-gray-200 bg-gray-50 overflow-hidden hover:ring-2 hover:ring-blue-300"
                          title={it.image ? 'Change image' : 'Add image'}
                          onClick={()=> { cancelHoverPreview(); setPreviewUrl(null); setPickerFor(i); }}
                        >
                          {it.image ? (
                            <img src={it.image} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <svg className="h-5 w-5 m-auto text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 15l4-4 4 4 5-5 4 4"/></svg>
                          )}
                        </button>
                        {it.image && (
                          <button
                            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white border border-gray-200 shadow hover:bg-red-50"
                            title="Remove image"
                            onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setImageAt(i, undefined); }}
                            aria-label="Remove image"
                          >
                            <svg className="h-3.5 w-3.5 m-auto text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                          </button>
                        )}
                      </div>
                      <input
                        value={it.text}
                        onChange={e=>updateAt(i, e.target.value)}
                        className="flex-1 h-9 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      />
                      <button
                        className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-red-50"
                        title="Remove item"
                        aria-label="Remove item"
                        onClick={()=>removeAt(i)}
                      >
                        <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M8 6v12"/><path d="M16 6v12"/><path d="M5 6l1 14a2 2 0 002 2h8a2 2 0 002-2l1-14"/><path d="M10 6V4a2 2 0 012-2h0a2 2 0 012 2v2"/></svg>
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="p-4">
            <div className="text-xs text-gray-500 mb-2">Preview</div>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="h-64">
                <ListCel entries={entries} orientation={orientation} imageSize={imageSize} imageRadius={imageRadius} />
              </div>
            </div>
          </div>
        </div>
        <MediaPickerModal
          open={pickerFor !== null}
          onClose={()=> setPickerFor(null)}
          onSelect={(file)=> { const i = pickerFor; setPickerFor(null); if (i !== null) setImageAt(i, file.url); }}
        />
        {toast && (
          <div className="fixed top-4 inset-x-0 z-[140] flex justify-center pointer-events-none">
            <Toast message={toast.message} type={toast.type} onClose={()=> setToast(null)} />
          </div>
        )}
        {confirmDelete && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50" onMouseDown={()=> setConfirmDelete(null)}>
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-sm p-5" onMouseDown={e=>e.stopPropagation()}>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-red-50 ring-1 ring-red-200 flex items-center justify-center shrink-0">
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v4"/><path d="M12 17h.01"/><circle cx="12" cy="12" r="9"/></svg>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 mb-1">Delete list</div>
                  <div className="text-sm text-gray-600">Delete list "{confirmDelete}"? This cannot be undone.</div>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button className="h-9 px-3 rounded-md border border-gray-200 hover:bg-gray-50" onClick={()=> setConfirmDelete(null)}>Cancel</button>
                <button
                  className="h-9 px-3 rounded-md bg-red-600 text-white hover:bg-red-700"
                  onClick={async()=>{
                    const target = confirmDelete; if (!target) return;
                    try {
                      const ok = await deleteList(target);
                      if (!ok) { setToast({ message: 'Failed to delete list.', type: 'error' }); return; }
                      setToast({ message: 'List deleted.', type: 'success' });
                      const items = await listLists(); setAvailable(items.map(i => ({ name: i.name, mtime: i.mtime })));
                      if (listName === target) { setListName(''); setEntries([]); }
                    } catch { setToast({ message: 'Failed to delete list.', type: 'error' }); }
                    finally { setConfirmDelete(null); }
                  }}
                >Delete</button>
              </div>
            </div>
          </div>
        )}
        {renameTarget && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50" onMouseDown={()=> setRenameTarget(null)}>
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-sm p-5" onMouseDown={e=>e.stopPropagation()}>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-blue-50 ring-1 ring-blue-200 flex items-center justify-center shrink-0">
                  <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 mb-1">Rename list</div>
                  <div className="text-sm text-gray-600 mb-3">Enter a new name. It will be converted to a slug.</div>
                  <input
                    className="w-full h-9 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-300"
                    value={renameTarget.value}
                    onChange={e=> setRenameTarget(v => v ? ({ ...v, value: e.target.value }) : v)}
                    placeholder="new-name"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button className="h-9 px-3 rounded-md border border-gray-200 hover:bg-gray-50" onClick={()=> setRenameTarget(null)}>Cancel</button>
                <button
                  className="h-9 px-3 rounded-md bg-purple-600 text-white hover:bg-purple-700"
                  onClick={async()=>{
                    const t = renameTarget; if (!t) return;
                    const newName = (t.value || '').toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,'');
                    if (!newName || newName === t.old) { setRenameTarget(null); return; }
                    try {
                      const ok = await renameList(t.old, newName);
                      if (!ok) { setToast({ message: 'Failed to rename list.', type: 'error' }); return; }
                      setToast({ message: 'List renamed.', type: 'success' });
                      const items = await listLists(); setAvailable(items.map(i => ({ name: i.name, mtime: i.mtime })));
                      if (listName === t.old) setListName(newName);
                    } catch { setToast({ message: 'Failed to rename list.', type: 'error' }); }
                    finally { setRenameTarget(null); }
                  }}
                >Rename</button>
              </div>
            </div>
          </div>
        )}
        {previewUrl && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60" onMouseDown={()=> setPreviewUrl(null)}>
            <div className="max-w-[90vw] max-h-[85vh]" onMouseDown={e=>e.stopPropagation()}>
              <img src={previewUrl} alt="Preview" className="max-w-[90vw] max-h-[85vh] rounded-xl shadow-2xl border border-white/20" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListEditor;
